export type MatchType = 'league' | 'playoff';

export type LostReason = 'ACE' | 'IN' | 'OUT' | 'TOUCH' | 'NET' | 'FOUL';

export type MatchStatus = 'scheduled' | 'setup' | 'first-server' | 'live' | 'set-won' | 'match-won' | 'discarded';

export type TossChoice = 'serve' | 'side';

export interface TournamentRules {
  leagueWinningPoint: number;
  leagueGoldenPoint: number;
  playoffWinningPoint: number;
  playoffGoldenPoint: number;
}

export interface Tournament {
  id: string;
  name: string;
  date: string;
  venue: string | null;
  leagueWinningPoint: number;
  leagueGoldenPoint: number;
  playoffWinningPoint: number;
  playoffGoldenPoint: number;
  createdAt: string;
  updatedAt: string;
}

export interface Player {
  id: string;
  name: string;
  number: number;
  teamId: string;
}

export interface Team {
  id: string;
  name: string;
  shortName: string;
  color: string;
  logoUrl?: string | null;
  players: Player[];
}

export interface Match {
  id: string;
  matchNumber: number;
  date: string;
  matchTime: string | null;
  matchType: MatchType;
  teamAId: string;
  teamBId: string;
  tossWonById: string;
  tossChoice: TossChoice;
  status: MatchStatus;
  winnerId: string | null;
  bestCatcherIds: string[];
  tournamentId: string | null;
  liveStreamUrl?: string | null;
  winningPoint: number | null;
  goldenPoint: number | null;
  isLocked: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SetRecord {
  id: string;
  matchId: string;
  setNumber: number;
  scoreA: number;
  scoreB: number;
  winnerId: string | null;
  startedAt: string;
  endedAt: string | null;
  rallies?: Rally[];
}

export interface Rally {
  id: string;
  setId: string;
  rallyNumber: number;
  servingTeamId: string;
  serverId: string;
  pointWonById: string;
  lostReason: LostReason | null;
  pointWinnerId: string | null;
  recordedAt: string;
}

export interface RotationState {
  serveHistory: string[];
  cycleServed: string[];
}

export interface TeamState {
  id: string;
  name: string;
  shortName: string;
  color: string;
  logoUrl?: string | null;
  players: Player[];
  setsWon: number;
  rotationState: RotationState;
}

export interface SetState {
  scoreA: number;
  scoreB: number;
  rallies: Rally[];
  servingTeamId: string;
  currentServerId: string;
  goldenPoint: boolean;
}

export interface MatchState {
  match: Match | null;
  teamA: TeamState | null;
  teamB: TeamState | null;
  currentSetNumber: number;
  currentSet: SetState | null;
  status: MatchStatus;
  sets: SetRecord[];
  bestCatcherIds: string[];
  tournamentRules?: TournamentRules | null;
}

export interface ValidationError {
  id: string;
  title: string;
  message: string;
}

export interface FirstServerSelection {
  teamId: string;
  teamName: string;
  players: Player[];
}

export interface ServerSelectionData {
  teamId: string;
  teamName: string;
  players: Player[];
  eligiblePlayerIds: string[];
  reason: 'first-server' | 'service-break';
}

export interface PointWinnerSelectionData {
  teamId: string;
  teamName: string;
  players: Player[];
  lostReason: LostReason;
}

export const MAX_SCORE = 21;
export const WIN_SCORE = 15;
export const WIN_DIFFERENCE = 2;
export const MIN_ROTATION_GAP = 3;

export const DEFAULT_TOURNAMENT_RULES: TournamentRules = {
  leagueWinningPoint: 15,
  leagueGoldenPoint: 21,
  playoffWinningPoint: 21,
  playoffGoldenPoint: 31,
};

export const MATCH_CONFIG: Record<MatchType, { maxSets: number; winsNeeded: number }> = {
  league: { maxSets: 3, winsNeeded: 2 },
  playoff: { maxSets: 5, winsNeeded: 3 },
};

export interface MatchRulesConfig {
  winningPoint: number;
  goldenPoint: number;
  maxSets: number;
  winsNeeded: number;
}

export function getMatchRules(matchType: MatchType, rules: TournamentRules | null, matchWinningPoint?: number | null, matchGoldenPoint?: number | null): MatchRulesConfig {
  const matchConfig = MATCH_CONFIG[matchType];

  // If match has its own rules (from creation time), use those
  if (matchWinningPoint !== null && matchWinningPoint !== undefined && matchGoldenPoint !== null && matchGoldenPoint !== undefined) {
    return {
      winningPoint: matchWinningPoint,
      goldenPoint: matchGoldenPoint,
      maxSets: matchConfig.maxSets,
      winsNeeded: matchConfig.winsNeeded,
    };
  }

  // If tournament rules exist, use those
  if (rules) {
    return matchType === 'league'
      ? {
          winningPoint: rules.leagueWinningPoint,
          goldenPoint: rules.leagueGoldenPoint,
          maxSets: matchConfig.maxSets,
          winsNeeded: matchConfig.winsNeeded,
        }
      : {
          winningPoint: rules.playoffWinningPoint,
          goldenPoint: rules.playoffGoldenPoint,
          maxSets: matchConfig.maxSets,
          winsNeeded: matchConfig.winsNeeded,
        };
  }

  // Fallback to defaults
  return matchType === 'league'
    ? {
        winningPoint: DEFAULT_TOURNAMENT_RULES.leagueWinningPoint,
        goldenPoint: DEFAULT_TOURNAMENT_RULES.leagueGoldenPoint,
        maxSets: matchConfig.maxSets,
        winsNeeded: matchConfig.winsNeeded,
      }
    : {
        winningPoint: DEFAULT_TOURNAMENT_RULES.playoffWinningPoint,
        goldenPoint: DEFAULT_TOURNAMENT_RULES.playoffGoldenPoint,
        maxSets: matchConfig.maxSets,
        winsNeeded: matchConfig.winsNeeded,
      };
}

export const LOST_REASONS: LostReason[] = ['ACE', 'IN', 'OUT', 'TOUCH', 'NET', 'FOUL'];

export const LOST_REASON_REQUIRES_WINNER: Record<LostReason, boolean> = {
  ACE: false,
  IN: true,
  OUT: false,
  TOUCH: true,
  NET: false,
  FOUL: false,
};

export const LOST_REASON_LABELS: Record<LostReason, string> = {
  ACE: 'ACE',
  IN: 'IN',
  OUT: 'OUT',
  TOUCH: 'TOUCH',
  NET: 'NET',
  FOUL: 'FOUL',
};

// Timeout types
export interface ActiveTimeout {
  id: string;
  teamId: string;
  teamName: string;
  setNumber: number;
  startTimestamp: string;
}
